/**
 * NotificationBell — top-nav inbox for @mentions, assignments, and watched-
 * task activity. Lives in AppShell's right cluster, left of the user
 * identity cluster. §2.5 anatomy: a filled Ø36 --surface-2 circle (no
 * stroke) inside a 44×44 hit target, with the --sem-orange organic-pill
 * unread badge overlapping the circle's top-right corner; the
 * dropdown is a plain absolute-positioned panel (NOT portaled) — unlike
 * ProjectsMenu (which sits inside the horizontally-scrolling nav row and
 * would otherwise be clipped), the right cluster and its ancestor header
 * have no `overflow` set, so nothing clips an absolutely-positioned child
 * here. CreateMenu already relies on the same fact one sibling over.
 * Polling for `unreadCount` happens inside `useNotifications` (60s).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/api/notification';
import type { AppNotification, NotificationKind } from '@/types/notification';
import { formatRelativeTime } from '@/lib/relativeTime';

// ---------------------------------------------------------------------------
// Icons — mono glyphs / inline SVGs, matching the app's icon language
// (currentColor, 1.7-2 stroke width, round caps). No emoji.
// ---------------------------------------------------------------------------

function IconBell() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function KindGlyph({ kind }: { kind: NotificationKind }) {
  if (kind === 'MENTION') {
    return (
      <span aria-hidden="true" style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-sm)', lineHeight: 1 }}>
        @
      </span>
    );
  }
  if (kind === 'ASSIGNED') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );
  }
  // WATCH_COMMENT / WATCH_STATE — open-eye glyph
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Unread badge — §1.2/§2.5: the ONE sanctioned orange element. Organic pill
// ~26×18, r9, --sem-orange fill, 11/700 white digits, overlapping the bell
// circle's top-right by ~40%. Never lime (lime discipline §4.1.3).
// ---------------------------------------------------------------------------

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  const label = count > 9 ? '9+' : String(count);
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: -7,
        right: -9,
        minWidth: 26,
        height: 18,
        padding: '0 5px',
        borderRadius: 9,
        background: 'var(--sem-orange)',
        color: 'var(--on-orange)',
        fontFamily: 'var(--font-sans)',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: '18px',
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function NotificationRow({ notification, onSelect }: { notification: AppNotification; onSelect: (n: AppNotification) => void }) {
  const unread = !notification.readAt;
  return (
    <button
      type="button"
      data-testid={`notification-row-${notification.id}`}
      onClick={() => onSelect(notification)}
      style={{
        display: 'flex',
        width: '100%',
        boxSizing: 'border-box',
        gap: 10,
        textAlign: 'left',
        padding: '10px 12px',
        background: unread ? 'var(--brand-soft)' : 'transparent',
        cursor: 'pointer',
        // Explicit longhand sides (not the `border` shorthand) — a shorthand
        // reset here would zero out the color set on the opposite side too.
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: unread ? 'var(--brand)' : 'transparent',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = unread ? 'var(--brand-soft)' : 'var(--surface-inset)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = unread ? 'var(--brand-soft)' : 'transparent';
      }}
    >
      <span style={{ flexShrink: 0, width: 16, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-subtle)' }}>
        <KindGlyph kind={notification.kind} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: unread ? 700 : 500, color: 'var(--text)' }}>
            {notification.actorName ?? 'Someone'}
          </span>
          {notification.taskKey && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-2xs)',
                padding: '1px 6px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-xs)',
                color: 'var(--text-muted)',
              }}
            >
              {notification.taskKey}
            </span>
          )}
        </div>
        <p
          style={
            {
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } as CSSProperties
          }
        >
          {notification.excerpt}
        </p>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xs)', color: 'var(--text-subtle)' }}>
          {formatRelativeTime(notification.createdAt)}
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// NotificationBell
// ---------------------------------------------------------------------------

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data, isLoading, isError } = useNotifications(false);
  const items = data?.items ?? [];
  // On error the badge hides rather than showing a fake "0" or a stale
  // prior count as if it were still accurate — the dropdown's error row
  // carries the actual explanation (silent-failure audit finding 11).
  const unreadCount = isError ? 0 : (data?.unreadCount ?? 0);

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleSelect(n: AppNotification) {
    markRead.mutate({ id: n.id });
    setOpen(false);
    if (n.taskId && n.projectId) {
      navigate(`/project/${n.projectId}?tab=board&task=${n.taskId}`);
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring pressable"
        // 44×44 hit target (§4.2) wrapping the Ø36 visual circle below.
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          boxSizing: 'border-box',
          padding: 0,
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          background: 'transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          const circle = (e.currentTarget as HTMLButtonElement).querySelector('span');
          if (circle) (circle as HTMLSpanElement).style.background = 'var(--surface-3)';
        }}
        onMouseLeave={(e) => {
          const circle = (e.currentTarget as HTMLButtonElement).querySelector('span');
          if (circle) (circle as HTMLSpanElement).style.background = 'var(--icon-circle-bg)';
        }}
      >
        {/* §2.5 — filled Ø36 circle, luminance step, no stroke. QA R2:
            --icon-circle-bg (not --surface-2) — --surface-2 was ~1.0:1
            against the light topbar bg (imperceptible); the token darkens
            one step in light while staying --surface-2 in dark. */}
        <span
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--icon-circle-bg)',
            color: 'var(--text-mid)',
            transition: 'background 0.12s',
          }}
        >
          <IconBell />
          <UnreadBadge count={unreadCount} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          className="pop-in"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 360,
            maxHeight: 480,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 30,
            ['--pop-origin' as string]: 'top right',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
              position: 'sticky',
              top: 0,
              background: 'var(--surface)',
            }}
          >
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="focus-ring"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  color: 'var(--brand)',
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {isLoading ? (
            <div
              style={{
                padding: '24px 14px',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-subtle)',
              }}
            >
              Loading…
            </div>
          ) : isError ? (
            // Distinct from the empty state below — an empty inbox and a
            // failed fetch read very differently ("nothing to show" vs
            // "couldn't check"). Polling (useNotifications' refetchInterval)
            // keeps running underneath this, hence "retrying…" rather than a
            // dead end.
            <div
              role="alert"
              data-testid="notification-error-row"
              style={{
                padding: '24px 14px',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--error)',
              }}
            >
              Couldn&apos;t load notifications — retrying…
            </div>
          ) : items.length === 0 ? (
            <div
              style={{
                padding: '24px 14px',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-subtle)',
              }}
            >
              No notifications yet.
            </div>
          ) : (
            items.map((n) => <NotificationRow key={n.id} notification={n} onSelect={handleSelect} />)
          )}
        </div>
      )}
    </div>
  );
}
